import { useState, useEffect } from "react";
import Badge, { BadgeColors } from "../shared-components/Badge";
import { classNames } from "../utils";
import { WorkflowByStepSummaryItem } from "../api/Workflows";
import { ReportSummaryApi } from "../api/Reports";

export enum ReportEntryStyle {
  TEXT = "TEXT",
  BOLD_TEXT = "BOLD_TEXT",
  BADGE = "BADGE",
  CANCEL = "CANCEL",
  COMPONENT = "COMPONENT",
}

export interface ReportEntry {
  text: string;
  style: ReportEntryStyle;
  badgeColor?: BadgeColors;
  component?: JSX.Element;
  customClass?: string;
}

export interface ReportRowItem {
  report_uuid: string;
  report: WorkflowByStepSummaryItem | ReportSummaryApi;
  data: ReportEntry[];
  isLocked?: boolean;
}

interface TableProps {
  data: ReportRowItem[];
  header: Array<string>;
}

const Table = ({ data, header }: TableProps) => {
  const [tableData, setTableData] = useState<ReportRowItem[]>(
    data !== undefined ? data : []
  );

  useEffect(() => {
    let table: ReportRowItem[] = [];
    if (data) {
      table = [...data];
    }
    setTableData(table);
  }, [data]);

  return (
    <>
      <div>
        <div className="flex text-gray-500 rounded-t-xl border shadow shadow-gray-300">
          {header.map((header) => (
            <div
              className="flex rounded-t-xl items-center p-3 text-xs leading-4 font-medium bg-gray-50 uppercase flex-1"
              key={header}
            >
              {header}
            </div>
          ))}
        </div>
        <div className="bg-white border-l border-r rounded-b-xl shadow shadow-gray-300">
          {tableData.length === 0 ? (
            <div>No search Items</div>
          ) : (
            tableData.map((report, i) => (
              <ReportRow reportRow={report} index={i} key={i} />
            ))
          )}
        </div>
      </div>
    </>
  );
};

interface ReportRowProps {
  reportRow: ReportRowItem;
  index: number;
}

const ReportRow = ({ reportRow, index }: ReportRowProps) => {
  const createEntry = (entry: ReportEntry, key: string) => {
    switch (entry.style) {
      case ReportEntryStyle.COMPONENT:
        return (
          <div
            key={key}
            className={classNames(
              "flex px-3 gap-2 items-center truncate",
              entry.customClass ? entry.customClass : "flex-1 w-4"
            )}
          >
            {entry.component}
          </div>
        );
      case ReportEntryStyle.BADGE:
        return (
          <div key={key} className="flex px-3 flex-1 gap-2 items-center">
            <Badge color={entry.badgeColor || "default"} hasDot={false}>
              {entry.text}
            </Badge>
          </div>
        );
      case ReportEntryStyle.BOLD_TEXT:
        return (
          <div
            key={key}
            className="flex px-3 flex-1 font-semibold items-center text-gray-900"
          >
            {entry.text}
          </div>
        );
      case ReportEntryStyle.TEXT:
      default:
        return (
          <div key={key} className="flex px-3 flex-1 items-center">
            {entry.text}
          </div>
        );
    }
  };

  return (
    <div
      className={classNames(
        "flex border-b text-xs leading-4 font-normal py-4 text-gray-500",
        reportRow.isLocked ? "bg-gray-100" : ""
      )}
      key={reportRow.report_uuid || `report-${index}`}
    >
      {reportRow.data.map((entry: ReportEntry, j) =>
        createEntry(entry, `report-${index}-entry-${j}`)
      )}
    </div>
  );
};

export default Table;
